// Immediate execution

/**
 * GiftCoordinator - Manages gift operations in a queue to prevent race conditions
 * and reduce latency by batching cart updates.
 */

class GiftCoordinator {
  constructor() {
    if (GiftCoordinator.instance) {
      return GiftCoordinator.instance;
    }
    GiftCoordinator.instance = this;

    // Initialize flags for each gift type
    this.handlingFreeGift = false;
    this.handlingSecondFreeGift = false;
    this.handlingThirdFreeGift = false;
    this.handlingFourthFreeGift = false;
    this.isProcessing = false;
    this.isProcessingThresholds = false;

    this.config = window.shopifyConfig || {};
    
    this.queue = [];
    
    this.debugMode = window.themeSettings?.debug_mode || false;
    this.initialized = false;
    
    this.bindEvents();
  }

  bindEvents() {
    // DISABLED FOR CLEAN SLATE - START
    /*
    document.addEventListener('gift:request', (event) => {
      this.enqueueGiftRequest(event.detail);
    });

    document.addEventListener('cart:refresh', async () => {
      const cart = await this.fetchCart();
      await this.checkThresholdGifts(cart);
    });
    */
    // DISABLED FOR CLEAN SLATE - END
  }

  enqueueGiftRequest(request) {
    const queueItem = {
      ...request,
      timestamp: Date.now()
    };
    this.queue.push(queueItem);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      const currentCart = await this.fetchCart();
      
      const requests = [...this.queue];
      this.queue = [];

      const grouped = this.groupRequests(requests);
      
      for (const [type, items] of Object.entries(grouped)) {
        await this.processRequestGroup(type, items, currentCart);
      }

      const updatedCart = await this.fetchCart();
      
      await this.checkThresholdGifts(updatedCart);
      
      document.dispatchEvent(new CustomEvent('gift:completed', {
        detail: { 
          success: true,
          cart: updatedCart
        }
      }));

      document.dispatchEvent(new CustomEvent('cartUpdated', {
        detail: { source: 'gift-coordinator' }
      }));

    } catch (error) {
      document.dispatchEvent(new CustomEvent('gift:completed', {
        detail: { 
          success: false,
          error: error.message
        }
      }));
    } finally {
      this.isProcessing = false;
      
      if (this.queue.length > 0) {
        this.processQueue();
      }
    }
  }

  groupRequests(requests) {
    return requests.reduce((groups, request) => {
      const { type } = request;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(request);
      return groups;
    }, {});
  }

  async processRequestGroup(type, requests, cart) {
    switch (type) {
      case 'modal':
        await this.processModalGifts(requests, cart);
        break;
      case 'threshold':
        await this.processThresholdGifts(requests, cart);
        break;
    }
  }

  async processModalGifts(requests, cart) {
    for (const request of requests) {
      const { variantId, quantity = 1, properties } = request;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity,
            properties
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add gift to cart');
        }

        const result = await response.json();
      } catch (error) {
        throw error;
      }
    }
  }

  async processThresholdGifts(requests, cart) {
    const updates = {};
    const cartTotal = this.calculateCartTotal(cart);
    
    for (const request of requests) {
      const { variantId, threshold = 0, quantity = 1 } = request;
      
      // Always process removal requests
      if (quantity === 0) {
        updates[variantId] = 0;
        continue;
      }

      // Add gift if threshold met
      if (cartTotal >= threshold) {
        updates[variantId] = (updates[variantId] || 0) + quantity;
      }
    }

    if (Object.keys(updates).length > 0) {
      await this.updateCart({ updates });
    }
  }

  calculateCartTotal(cart) {
    const total = cart.items.reduce((total, item) => {
      // Exclude gift items from total
      if (this.isGiftItem(item)) {
        return total;
      }
      return total + item.final_line_price;
    }, 0);

    return total / 100;
  }

  isGiftItem(item) {
    const isGift = item.properties?._system_added || 
                  this.config.modalGiftVariants?.includes(item.variant_id) ||
                  this.config.thresholdGiftVariants?.includes(item.variant_id);
    
    return isGift;
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return response.json();
  }

  async updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  async checkThresholdGifts(cart) {
    if (this.isProcessingThresholds) {
      return;
    }

    this.isProcessingThresholds = true;
    try {
      // Calculate true cart total FIRST, before any gift processing
      const trueCartTotal = cart.items.reduce((total, item) => {
        // Only count non-gift items
        if (!item.properties?._gift_type && !item.properties?._system_added) {
          return total + item.final_line_price;
        }
        return total;
      }, 0) / 100;

      // Check for starter kit first
      const hasStarterKit = cart.items.some(item => 
        String(item.variant_id) === '51701354955122'
      );

      if (hasStarterKit) {
        return;
      }

      let cartModified = false;

      // First check if cart is empty or zero - remove all gifts if so
      if (trueCartTotal <= 0) {
        const giftsToRemove = cart.items.filter(item => {
          // Remove threshold gifts
          if (item.properties?._gift_type === 'threshold_gift') {
            return true;
          }
          
          // Remove modal gifts ONLY if they don't have Klaviyo source protection
          if (item.properties?._gift_type === 'modal_gift') {
            return item.properties?._klaviyo_source !== true;
          }
          
          return false;
        });

        for (const gift of giftsToRemove) {
          await this.removeItemFromCart(gift.key);
          cartModified = true;
        }

        if (cartModified) {
          const finalCart = await this.fetchCart();
          document.dispatchEvent(new CustomEvent('gift:completed', {
            detail: { 
              success: true,
              cart: finalCart
            }
          }));
          document.dispatchEvent(new CustomEvent('cartUpdated', {
            detail: { 
              source: 'gift-coordinator',
              skipRefresh: true
            }
          }));
        }
        return;
      }

      // Get threshold settings and sort by threshold amount
      const thresholds = [...(this.config.thresholdGifts || [])].sort((a, b) => a.threshold - b.threshold);

      for (const threshold of thresholds) {
        const { variantId, threshold: minAmount, label } = threshold;
        
        if (!variantId || !minAmount) continue;
        
        const meetsThreshold = trueCartTotal >= minAmount;
        
        const hasThresholdGift = cart.items.some(item => {
          const isMatch = String(item.variant_id) === String(variantId) && 
                         item.properties?._gift_type === 'threshold_gift';
          return isMatch;
        });

        // Use separate flags for each threshold to prevent race conditions
        const handlingFlag = `handling${label.replace(/\s+/g, '')}Gift`;
        if (this[handlingFlag]) {
          continue;
        }

        try {
          this[handlingFlag] = true;

          if (meetsThreshold && !hasThresholdGift) {
            await this.addGiftToCart({
              variantId,
              quantity: 1,
              properties: {
                _gift_type: 'threshold_gift',
                _system_added: true,
                _threshold_amount: minAmount,
                _threshold_label: label
              }
            });
            cartModified = true;
          } else if (!meetsThreshold && hasThresholdGift) {
            const itemToRemove = cart.items.find(item => 
              String(item.variant_id) === String(variantId) && 
              item.properties?._gift_type === 'threshold_gift'
            );
            if (itemToRemove) {
              await this.removeItemFromCart(itemToRemove.key);
              cartModified = true;
            }
          }
        } finally {
          this[handlingFlag] = false;
        }
      }

      // Only dispatch events if cart was actually modified
      if (cartModified) {
        const finalCart = await this.fetchCart();
        
        document.dispatchEvent(new CustomEvent('gift:completed', {
          detail: { 
            success: true,
            cart: finalCart
          }
        }));

        document.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { 
            source: 'gift-coordinator',
            skipRefresh: true
          }
        }));
      }
    } finally {
      this.isProcessingThresholds = false;
    }
  }

  async addGiftToCart(giftDetails) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: giftDetails.variantId,
          quantity: giftDetails.quantity,
          properties: giftDetails.properties
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  async removeItemFromCart(itemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to remove item: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

// Initialize the coordinator
window.giftCoordinator = new GiftCoordinator(); 